import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Alert, Box, Button, Checkbox, Container, FormControlLabel, LinearProgress, Stack, TextField, Typography } from '@mui/material';
import { interviewRequest } from './api/journeyBotApi';
import { ROUTES } from './routes';

export default function InterviewPage() {
  const { id } = useParams();
  const [session, setSession] = useState(null);
  const [text, setText] = useState('');
  const [summary, setSummary] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const pending = useRef(null);
  const inFlight = useRef(false);
  const accept = useCallback(value => { setSession(value); setSummary(value.draftSummary); }, []);
  const reload = useCallback(async () => {
    setBusy(true); setError('');
    try {
      const saved = await interviewRequest(`/${id}`);
      if (pending.current && saved.revision > pending.current.revision) {
        if (saved.operationIds?.includes(pending.current.operationId)) setText('');
        else setError('The interview changed elsewhere. Your unsaved text is still here; review the saved conversation before sending it.');
        pending.current = null;
      }
      accept(saved);
    }
    catch (e) { setError(e.message); }
    finally { setBusy(false); }
  }, [id, accept]);
  useEffect(() => { reload(); }, [reload]);
  useEffect(() => {
    const guard = event => { if (text || busy || pending.current || (session?.status === 'review' && summary !== session.draftSummary)) { event.preventDefault(); event.returnValue = ''; } };
    window.addEventListener('beforeunload', guard);
    return () => window.removeEventListener('beforeunload', guard);
  }, [text, busy, session, summary]);
  async function command(action, retry = false) {
    if (inFlight.current) return;
    inFlight.current = true; setBusy(true); setError('');
    try {
      const body = retry ? pending.current : { operationId: crypto.randomUUID(), revision: session.revision, action, text: action === 'correct' ? summary : action === 'answer' ? text : '' };
      pending.current = body;
      const saved = await interviewRequest(`/${id}/commands`, body);
      pending.current = null; accept(saved); setText('');
      if (saved.pending) accept(await interviewRequest(`/${id}/respond`, {}));
    } catch (e) { setError(e.message); }
    finally { inFlight.current = false; setBusy(false); }
  }
  async function respond() {
    if (inFlight.current) return;
    inFlight.current = true; setBusy(true); setError('');
    try { accept(await interviewRequest(`/${id}/respond`, {})); }
    catch (e) { setError(e.message); }
    finally { inFlight.current = false; setBusy(false); }
  }
  const locked = busy || Boolean(pending.current);
  return <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50', color: 'text.primary' }}>
    <Container maxWidth="sm" sx={{ py: 4, textAlign: 'left' }}><Stack spacing={2}>
    <Typography variant="h4" component="h1">Your home-buying story</Typography>
    {busy && <><LinearProgress /><Typography role="status">Saving or preparing the next response… Please keep this page open.</Typography></>}
    {error && <Alert severity="error">{error}</Alert>}
    {pending.current && <Button disabled={busy} onClick={() => command('', true)}>Retry saving the same response</Button>}
    <Button disabled={busy} onClick={reload}>Reload saved interview</Button>
    {!session && !busy && <Typography>Use your invitation link and the account it was issued to.</Typography>}
    {session?.status === 'invited' && <>
      <Typography>This AI interviewer asks about a home you already bought, recently or years ago. Your experience helps the founder understand home-buying journeys. We will prepare your own private JourneyBot journey for you to review and keep.</Typography>
      <Typography>Aim for around 10–15 minutes; it may take more or less. You can skip any question, pause by closing the page after saving, or finish early.</Typography>
      <Typography>After you agree, your responses, AI replies, summary and corrections are saved to your account in Firebase. The founder can review and export them for research and manually prepare your journey with ChatGPT or Codex. The interviewer runs on a local Qwen model through our protected service. Avoid names, addresses, account numbers or other sensitive details.</Typography>
      <Typography>Your participation does not permit public publication of your story. Data is retained as needed for this experiment and service under our privacy policy; contact us for access, correction or deletion.</Typography>
      <Stack direction="row" spacing={2}><Link to={ROUTES.privacy}>Privacy policy</Link><Link to={ROUTES.contact}>Contact</Link></Stack>
      <FormControlLabel control={<Checkbox checked={agreed} onChange={e => setAgreed(e.target.checked)} />} label="I agree to this interview and the data use described above." />
      <Button variant="contained" disabled={!agreed || locked} onClick={() => command('consent')}>Agree and begin</Button>
    </>}
    {session && session.status !== 'invited' && <>
      <Typography>{Object.keys(session.coverage).length} of 12 topics visited · {session.status === 'active' ? 'Interview' : session.status === 'review' ? 'Summary review' : 'Complete'}</Typography>
      <LinearProgress variant="determinate" value={session.status === 'completed' ? 100 : Object.keys(session.coverage).length / 12 * 100} />
      <Box role="log" aria-label="Saved interview conversation" aria-live="polite">
        {session.turns.map(turn => <Box key={turn.id} sx={{ p: 2, mb: 1, bgcolor: turn.role === 'user' ? 'action.hover' : 'background.paper', border: 1, borderColor: 'divider', borderRadius: 2 }}>
          <Typography variant="caption">{turn.role === 'user' ? 'You' : 'AI interviewer'} · Saved</Typography>
          <Typography sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{turn.content}</Typography>
        </Box>)}
      </Box>
    </>}
    {session?.status === 'active' && <>
      {session.pending ? <Alert severity="info">Your answer is saved. The interviewer response is pending.<Button disabled={locked} onClick={respond}>Retry interviewer</Button></Alert> : <>
        <TextField label="Your answer" multiline minRows={3} value={text} disabled={locked} onChange={e => setText(e.target.value)} inputProps={{ maxLength: 2000 }} helperText="Saved after you send. Up to 2,000 characters; you can skip." />
        <Stack direction="row" spacing={1}><Button disabled={locked || !text.trim()} variant="contained" onClick={() => command('answer')}>Send answer</Button><Button disabled={locked} onClick={() => command('skip')}>Skip question</Button></Stack>
      </>}
      <Button disabled={locked} onClick={() => command('finish')}>Finish now and review summary</Button>
    </>}
    {session?.status === 'review' && <>
      <Typography>Review your account below. Unknowns and contradictions have not been resolved for you. Edit the summary to correct them if you wish.</Typography>
      <TextField label="Summary for review" multiline minRows={8} value={summary} onChange={e => setSummary(e.target.value)} inputProps={{ maxLength: 50000 }} disabled={locked} />
      <Button disabled={locked || !summary.trim() || summary === session.draftSummary || session.corrections.length >= 5} onClick={() => command('correct')}>Save corrected summary</Button>
      <Button variant="contained" disabled={locked || summary !== session.draftSummary} onClick={() => command('confirm')}>Confirm summary and complete interview</Button>
    </>}
    {session?.status === 'completed' && <Alert severity="success">Your interview is complete. {session.deliveryStatus === 'delivered' ? <>Your private journey is ready. <a href={ROUTES.mainView}>Open your journeys</a> and select the returned home-buying journey.</> : 'Your journey is being prepared. Return to this page to check for it; no delivery time is promised.'}</Alert>}
    </Stack></Container>
  </Box>;
}
